import type {
  IControlSettingData,
  IDynamicControlSetting,
} from '~/presentation/reports/common/type';
import type { ReportCampaignParamsType } from './preset';

import { EReportControlId, ERFilterKey, EDimensionKey } from '~/presentation/reports/common/type';
import { getDimensionSettingUIs } from '../dimension-preset';
import { getFilterSettingUIs } from '../filter-preset';
import { ICampaignType } from '~/modules/campaign';
import { EReportPresetKey } from '../../types';
import { deepCopy } from '~/utils';

export function buildPresetForCampaign(
  defaultPreset: {
    presetData: IControlSettingData;
    presetUI: IDynamicControlSetting;
  },
  campaign: ReportCampaignParamsType,
  presetKey: EReportPresetKey,
) {
  const { campaignId } = campaign;
  if (!campaignId) return defaultPreset;

  const { dimensionValues, dimensionUIs } = removeTimeDimension(presetKey, defaultPreset);
  return {
    presetData: {
      ...defaultPreset.presetData,
      ...buildDataByCampaignType(campaign),
      [EReportControlId.FILTER]: {
        value: [ERFilterKey.EXPERIMENT],
      },
      [EReportControlId.DIMENSIONS_FILTER]: {
        value: dimensionValues
          ? [...dimensionValues, EDimensionKey.VERSION]
          : [EDimensionKey.VERSION],
      },
    },
    presetUI: {
      ...defaultPreset.presetUI,
      [EReportControlId.FILTER]: {
        controls: getFilterSettingUIs([ERFilterKey.DATE, ERFilterKey.EXPERIMENT]),
      },
      [EReportControlId.DIMENSIONS_FILTER]: {
        controls: [...dimensionUIs, ...getDimensionSettingUIs([EDimensionKey.VERSION])],
      },
    },
  };
}

function removeTimeDimension(
  presetKey: EReportPresetKey,
  defaultPreset: {
    presetData: IControlSettingData;
    presetUI: IDynamicControlSetting;
  },
) {
  const dimensionValues = deepCopy(
    defaultPreset.presetData[EReportControlId.DIMENSIONS_FILTER].value || [],
  );
  const dimensionUIs = deepCopy(
    defaultPreset.presetUI[EReportControlId.DIMENSIONS_FILTER].controls || [],
  );
  const PRESETS_REQUIRE_REMOVE_TIME = [
    EReportPresetKey.SESSION_BY_DEVICE_TYPE,
    EReportPresetKey.SESSION_BY_TRAFFIC_SOURCE,
    EReportPresetKey.VISITOR_BY_TYPE,
  ];
  const shouldRemoveTime = PRESETS_REQUIRE_REMOVE_TIME.includes(presetKey);

  if (!shouldRemoveTime) {
    return {
      dimensionValues,
      dimensionUIs,
    };
  }

  const timeIndex = dimensionValues.indexOf(EDimensionKey.TIME);
  if (timeIndex !== -1 && Array.isArray(dimensionValues)) {
    dimensionValues.splice(timeIndex, 1);
  }

  const timeIndexUI = dimensionUIs.findIndex((item) => item.id === EDimensionKey.TIME);
  if (timeIndexUI !== -1) {
    dimensionUIs.splice(timeIndexUI, 1);
  }

  return {
    dimensionValues,
    dimensionUIs,
  };
}

const buildDataByCampaignType = (campaign: ReportCampaignParamsType): IControlSettingData => {
  const { campaignType, campaignId, campaignGroupId } = campaign;
  const defaultTab = getTabByCampaignType(campaignType);

  if (!campaignId || !campaignType) return {};

  if (campaignType === ICampaignType.PRICE) {
    return {
      [EReportControlId.PRICE_CAMPAIGN]: {
        value: campaignId,
      },
      [EReportControlId.TAB_CAMPAIGN]: {
        value: campaignId,
        defaultTab,
      },
    };
  }

  if (campaignType === ICampaignType.URL) {
    return {
      [EReportControlId.URL_CAMPAIGN]: {
        value: campaignId,
      },
      [EReportControlId.TAB_CAMPAIGN]: {
        value: campaignId,
        defaultTab,
      },
    };
  }

  if (campaignType === ICampaignType.TEMPLATE) {
    return {
      [EReportControlId.SINGLE_CAMPAIGN]: {
        value: campaignId,
      },
      [EReportControlId.TAB_CAMPAIGN]: {
        value: campaignId,
        defaultTab,
      },
    };
  }

  //Case: is campaign group
  if (!campaignGroupId) {
    return {
      [EReportControlId.GROUP_CAMPAIGN]: {
        value: campaignId,
      },
      [EReportControlId.TAB_CAMPAIGN]: {
        value: campaignId,
        defaultTab,
      },
    };
  }

  //Case: is child campaign of campaign group
  return {
    [EReportControlId.GROUP_CAMPAIGN]: {
      value: campaignGroupId,
    },
    [EReportControlId.GROUP_CAMPAIGN_ITEMS]: {
      value: [campaignId],
    },
    [EReportControlId.TAB_CAMPAIGN]: {
      value: campaignGroupId,
      defaultTab,
    },
  };
};

const getTabByCampaignType = (campaignType: string | null): EReportControlId => {
  if (campaignType === ICampaignType.PRICE) return EReportControlId.PRICE_CAMPAIGN;
  if (campaignType === ICampaignType.URL) return EReportControlId.URL_CAMPAIGN;
  if (campaignType === ICampaignType.TEMPLATE) return EReportControlId.SINGLE_CAMPAIGN;

  return EReportControlId.GROUP_CAMPAIGN;
};
